import { Col, Icon } from 'components';
import { Api, theme, findProcessingSubscription } from 'lib';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import useSubscriptionPolling from 'hooks/useSubscriptionPolling';
import SubscriptionCard from './SubscriptionCard';
import SubscriptionDetailModal from './SubscriptionDetailModal';

const TeamSubscription = () => {
    const { isMobile } = useSelector((state) => state.appState);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [selectedSub, setSelectedSub] = useState(null);
    const [subscriptions, setSubscriptions] = useState([]);
    const [statusFilter, setStatusFilter] = useState('all');
    const [loading, setLoading] = useState(true);

    const paymentUpdated = new URLSearchParams(location.search).get('payment_updated') === '1';

    // Find any processing subscription for polling
    const processingSubscription = findProcessingSubscription(subscriptions);
    
    // Poll for status changes on processing subscriptions
    useSubscriptionPolling(processingSubscription, (updatedSub) => {
        // Refresh subscription list when status changes
        fetchStatus();
    });

    async function fetchStatus() {
        setLoading(true);
        const res = await Api.get('/subscriptionPlans/status');
        
        if (res?.data) {
            const items = Array.isArray(res.data) ? res.data : [res.data];
            setSubscriptions(
                items.map((s) => ({
                    ...s,
                    status:
                        String(s.status || '').toLowerCase() === 'success'
                            ? 'active'
                            : String(s.status || '').toLowerCase()
                }))
            );
        }
        setLoading(false);
    }

    useEffect(() => {
        fetchStatus();
    }, []);

    const renderContent = () => {
        if (loading) return null;

        // --- No active subscriptions ---
        if (!subscriptions || subscriptions.length === 0) {
            return (
                <Col
                    className="w-full"
                    marg="0 0 40px"
                    centerAll
                    noFlex
                >
                    <div className={`w-full bg-white rounded-xl border border-gray-200 flex flex-col items-center ${isMobile ? 'px-4 py-8' : 'px-8 py-10'}`}>
                        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-gray-50 mb-3">
                            <Icon type="receipt" col={theme.GREY_30} dimensions={24} />
                        </div>

                        <p className="text-base font-bold text-gray-900 font-[CeraPro]">{t('billing.noActiveSubscription')}</p>

                        <p className="text-sm text-gray-500 font-[CeraPro] mt-1 text-center">
                            {t('billing.subscribeToUnlock')}
                        </p>
                    </div>
                </Col>
            );
        }

        const statusCounts = subscriptions.reduce((acc, sub) => {
            const key = String(sub.status || '').toLowerCase();
            acc[key] = (acc[key] || 0) + 1;
            return acc;
        }, {});

        const filterOptions = [
            { key: 'all', label: t('common.all', { defaultValue: 'All' }), count: subscriptions.length },
            ...Object.keys(statusCounts).sort().map((status) => ({
                key: status,
                label: t(`billing.status_${status}`, { defaultValue: status }),
                count: statusCounts[status]
            }))
        ];

        const filteredSubscriptions =
            statusFilter === 'all'
                ? subscriptions
                : subscriptions.filter((sub) => String(sub.status || '').toLowerCase() === statusFilter);

        return (
            <Col className="w-full" gap="12px">
                <div className="w-full overflow-x-auto no-scrollbar">
                    <div className="flex items-center gap-2 min-w-max">
                        {filterOptions.map((option) => {
                            const selected = statusFilter === option.key;
                            return (
                                <button
                                    key={option.key}
                                    onClick={() => setStatusFilter(option.key)}
                                    className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full border text-xs font-bold font-[CeraPro] transition-all whitespace-nowrap ${
                                        selected
                                            ? 'border-black bg-black text-white'
                                            : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                                    }`}
                                >
                                    <span className="capitalize">{option.label}</span>
                                    <span className={`${selected ? 'text-white/80' : 'text-gray-500'}`}>
                                        ({option.count})
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {filteredSubscriptions.length === 0 && (
                    <div className="w-full bg-white rounded-xl border border-gray-200 px-5 py-6 text-center">
                        <p className="text-sm text-gray-700 font-medium font-[CeraPro]">
                            {t('billing.noSubscriptionsInFilter', { defaultValue: 'No subscriptions match this filter.' })}
                        </p>
                    </div>
                )}

                {filteredSubscriptions.map((sub) => (
                    <SubscriptionCard
                        key={sub.subscriptionId}
                        sub={sub}
                        isMobile={isMobile}
                        onOpenDetails={setSelectedSub}
                    />
                ))}

                <SubscriptionDetailModal
                    visible={selectedSub !== null}
                    closeModal={() => setSelectedSub(null)}
                    sub={selectedSub}
                    isMobile={isMobile}
                />
            </Col>
        );
    };

    return (
        <div className={`w-full ${isMobile ? 'px-4' : ''}`}>
            {paymentUpdated && (
                <div className="flex items-start gap-2 w-full bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 mb-4">
                    <Icon type="info" col="#3b82f6" dimensions={16} style={{ flexShrink: 0, marginTop: '2px' }} />
                    <div>
                        <p className="text-sm font-bold text-blue-900 font-[CeraPro]">
                            {t('billing.paymentUpdatedTitle')}
                        </p>
                        <p className="text-xs text-blue-700 font-[CeraPro] mt-0.5">
                            {t('billing.paymentUpdatedMessage')}
                        </p>
                    </div>
                </div>
            )}
            {renderContent()}
        </div>
    );
};

export default TeamSubscription;
